import { Reference } from '@approvalmax/types';
import { errorHelpers } from '@approvalmax/utils';
import { AsyncDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { useCallback } from 'react';
import { useLazyGetNetSuiteCustomers } from 'shared/data/webApp/v2';

import { requestSelector } from '../../../../selectors/pageSelectors';
import { convertIntegrationCodeToDocumentType } from '../../helpers';
import { NetSuiteCustomersDataProviderProps } from './NetSuiteCustomersDataProvider.types';

export const NetSuiteCustomersDataProvider = (props: NetSuiteCustomersDataProviderProps) => {
    const { children, integrationCode, subsidiaryId } = props;

    const request = useSelector(requestSelector);

    const { companyId, id: requestId } = request;

    const documentType = convertIntegrationCodeToDocumentType(integrationCode);

    const [triggerGetNetSuiteCustomers] = useLazyGetNetSuiteCustomers();

    const onLoad = useCallback(
        async (filterText: string | null) => {
            try {
                const response = await triggerGetNetSuiteCustomers({
                    params: {
                        path: {
                            companyId,
                            documentType,
                        },
                        query: {
                            subsidiaryId,
                            query: filterText ?? undefined,
                        },
                    },
                });

                return response.reduce<Reference[]>((customers, customer) => {
                    if (customer.id && customer.name) {
                        customers.push({
                            id: customer.id,
                            text: customer.name,
                        });
                    }

                    return customers;
                }, []);
            } catch (error) {
                errorHelpers.captureException(error);
            }

            return [];
        },
        [companyId, documentType, subsidiaryId, triggerGetNetSuiteCustomers]
    );

    return (
        <AsyncDataProvider
            cacheStorageId={`requestOptions.netSuiteCustomers.${requestId}`}
            pageSize={100}
            onLoad={onLoad}
        >
            {children}
        </AsyncDataProvider>
    );
};

NetSuiteCustomersDataProvider.displayName = 'NetSuiteCustomersDataProvider';
